/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Table,
  Modal,
  Form,
  Input,
  Select,
  Upload,
  Button,
  message,
  Breadcrumb,
  Switch,
  Tag,
  Tooltip,
  Dropdown,
  Menu,
  Space,
  Image,
  Empty,
} from "antd";
import {
  AiOutlineUpload,
  AiOutlineSearch,
  AiOutlinePlus,
  AiOutlineReload,
  AiOutlineEdit,
  AiOutlineDelete,
  AiOutlinePicture,
  AiOutlineMore,
} from "react-icons/ai";
import { Link } from "react-router-dom";
import axios from "axios";

import {
  getPages,
  createPage,
  updatePage,
  deletePage,
  uploadFile,
  getMenuAll,
  createMenu,
  updateMenu,
  deleteMenu,
} from "../../../services/cms";
import RichTextEditor from "./RichTextEditor";
import "./cms-pages.css";

const { Option } = Select;

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9/]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/\/+/g, "/");

// Clean a user-typed slug to the exact form the backend stores / the URL
// produces: no leading/trailing slash, no spaces, lowercase. A stray leading
// "/" here is what makes a published page un-findable on the public site.
const normalizeSlugInput = (s) =>
  (s || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/^\/+|\/+$/g, "")
    .replace(/\/{2,}/g, "/");

// Every nav menu item (there should be at most one) that links to a page.
const findLinkedMenus = (pageId, items) =>
  (items || []).filter(
    (m) =>
      m.type === "page" &&
      m.page &&
      String(m.page._id || m.page) === String(pageId)
  );
const findLinkedMenu = (pageId, items) => findLinkedMenus(pageId, items)[0];

// A menu item's id plus all of its descendants — used to keep a link from being
// reparented under itself (which would orphan it).
const collectSubtreeIds = (rootId, items) => {
  const ids = new Set();
  if (!rootId) return ids;
  ids.add(String(rootId));
  const list = items || [];
  let added = true;
  while (added) {
    added = false;
    for (let i = 0; i < list.length; i += 1) {
      const m = list[i];
      const pid = String(m.parent || "");
      if (pid && ids.has(pid) && !ids.has(String(m._id))) {
        ids.add(String(m._id));
        added = true;
      }
    }
  }
  return ids;
};

const CmsPages = () => {
  const [form] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [body, setBody] = useState("");
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerUrl, setBannerUrl] = useState("");
  const [bannerPreview, setBannerPreview] = useState("");
  const [bannerType, setBannerType] = useState("image");
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  // ---- "Show in navigation" (opt-in) ----
  const [menuItems, setMenuItems] = useState([]);
  const [showInNav, setShowInNav] = useState(false);
  const [navParent, setNavParent] = useState("top:main-menu");
  const [linkedMenuId, setLinkedMenuId] = useState(null);
  // navLoaded: did we reliably learn this page's current nav state? (guards us
  // from deleting a link we never actually confirmed). navTouchedRef: has the
  // user changed the nav controls this session (so an async refresh won't clobber).
  const [navLoaded, setNavLoaded] = useState(true);
  const navTouchedRef = useRef(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getPages({ viewSize: 500 });
      setRows(data.pages || []);
    } catch (e) {
      message.error("Failed to load pages");
    }
    setLoading(false);
  };

  const loadMenu = async () => {
    try {
      const data = await getMenuAll();
      setMenuItems(data.menu || []);
    } catch (e) {
      /* navigation linking is optional; ignore load errors */
    }
  };

  useEffect(() => {
    load();
    loadMenu();
  }, []);

  // Derive the nav controls from a set of menu items for the given page.
  const applyNavState = (pageId, items) => {
    const linked = findLinkedMenu(pageId, items);
    setShowInNav(!!linked);
    setLinkedMenuId(linked ? linked._id : null);
    setNavParent(
      linked
        ? linked.parent
          ? String(linked.parent)
          : `top:${linked.group || "main-menu"}`
        : "top:main-menu"
    );
  };

  // Live preview URL for a freshly-picked banner file.
  useEffect(() => {
    if (!bannerFile) {
      setBannerPreview("");
      return undefined;
    }
    const url = URL.createObjectURL(bannerFile);
    setBannerPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [bannerFile]);

  const openAdd = () => {
    setEditingId(null);
    setBody("");
    setBannerFile(null);
    setBannerUrl("");
    setBannerType("image");
    setSlugTouched(false);
    setShowInNav(false);
    setNavParent("top:main-menu");
    setLinkedMenuId(null);
    setNavLoaded(true); // brand-new page: definitely no existing link
    navTouchedRef.current = false;
    form.resetFields();
    form.setFieldsValue({ status: "published", bannerType: "image" });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditingId(record._id);
    setBody(record.body || "");
    setBannerFile(null);
    setBannerUrl((record.banner && record.banner.url) || "");
    setBannerType((record.banner && record.banner.type) || "image");
    setSlugTouched(true);
    navTouchedRef.current = false;
    // Best-guess nav state from the current snapshot, then confirm from a fresh
    // fetch so a stale/empty snapshot can't show a wrong toggle (and later drop a
    // real link or create a duplicate).
    applyNavState(record._id, menuItems);
    setNavLoaded(menuItems.length > 0);
    getMenuAll()
      .then((data) => {
        const items = data.menu || [];
        setMenuItems(items);
        setNavLoaded(true);
        if (!navTouchedRef.current) applyNavState(record._id, items);
      })
      .catch(() => {});
    form.setFieldsValue({
      title: record.title,
      slug: record.slug,
      status: record.status || "published",
      bannerType: (record.banner && record.banner.type) || "image",
      bannerCaption: (record.banner && record.banner.caption) || "",
      metaTitle: (record.seo && record.seo.metaTitle) || "",
      metaDescription: (record.seo && record.seo.metaDescription) || "",
    });
    setModalOpen(true);
  };

  const onTitleChange = (e) => {
    if (!editingId && !slugTouched) {
      form.setFieldsValue({ slug: slugify(e.target.value) });
    }
  };

  // Resolve the chosen "Add under" value into a { group, parent } placement.
  const resolveNavPlacement = (items = menuItems) => {
    if (navParent.startsWith("top:")) {
      return { group: navParent.slice(4), parent: null };
    }
    const p = (items || []).find((m) => String(m._id) === String(navParent));
    return p
      ? { group: p.group || "main-menu", parent: p._id }
      : { group: "main-menu", parent: null };
  };

  // "Add under" options: top-level placements + existing top-level items, minus
  // this page's own link and its descendants (so it can't be its own parent).
  const navParentOptions = useMemo(() => {
    const blocked = collectSubtreeIds(linkedMenuId, menuItems);
    const opts = [
      {
        value: "top:primary-boards",
        label: "Top level — Board tabs row (CICSE / PYP / CAIE)",
      },
      { value: "top:main-menu", label: "Top level — Main menu" },
      { value: "top:footer", label: "Top level — Footer" },
    ];
    menuItems
      .filter((m) => !m.parent && !blocked.has(String(m._id)))
      .forEach((m) =>
        opts.push({ value: String(m._id), label: `Under ${m.label} (${m.group})` })
      );
    // Keep the current selection visible even if it points at a nested parent
    // (not in the top-level list), so the Select never shows a raw id.
    if (
      navParent &&
      !navParent.startsWith("top:") &&
      !opts.some((o) => String(o.value) === String(navParent))
    ) {
      const cur = menuItems.find((m) => String(m._id) === String(navParent));
      if (cur && !blocked.has(String(cur._id)))
        opts.push({
          value: String(cur._id),
          label: `Under ${cur.label} (${cur.group})`,
        });
    }
    return opts;
  }, [menuItems, linkedMenuId, navParent]);

  // Create / update / remove the nav menu item that links to this page, matching
  // the "Show in navigation" toggle. Secondary to the page save: a failure warns
  // but does not fail the save.
  const syncNavLink = async (pageId, values) => {
    if (!pageId) return;
    try {
      // Resolve the current link(s) from AUTHORITATIVE data at save time, so a
      // stale/empty snapshot can't create a duplicate or miss an existing link.
      let items = menuItems;
      try {
        items = (await getMenuAll()).menu || [];
        setMenuItems(items);
      } catch (e) {
        /* fall back to in-state snapshot */
      }
      const links = findLinkedMenus(pageId, items);

      if (showInNav) {
        const { group, parent } = resolveNavPlacement(items);
        // Collapse any accidental duplicates down to one.
        for (let i = 1; i < links.length; i += 1) {
          try {
            await deleteMenu(links[i]._id);
          } catch (e) {
            /* ignore */
          }
        }
        const primary = links[0];
        const siblingCount = items.filter(
          (m) =>
            (m.group || "main-menu") === group &&
            String(m.parent || "") === String(parent || "") &&
            (!primary || String(m._id) !== String(primary._id))
        ).length;
        if (primary) {
          const moved =
            (primary.group || "main-menu") !== group ||
            String(primary.parent || "") !== String(parent || "");
          await updateMenu(primary._id, {
            // Preserve a (possibly hand-curated) label; only retarget/reposition.
            label: primary.label || (values.title || "").trim(),
            group,
            parent,
            type: "page",
            page: pageId,
            url: "",
            fileUrl: "",
            order: moved ? siblingCount : primary.order || 0,
            openInNewTab: false,
            // isActive intentionally omitted: getTree already hides links to
            // draft/missing pages, and we must not clobber a manual flag set in
            // CMS Navigation.
          });
        } else {
          await createMenu({
            label: (values.title || "").trim(),
            group,
            parent,
            type: "page",
            page: pageId,
            url: "",
            fileUrl: "",
            order: siblingCount,
            openInNewTab: false,
            isActive: true,
          });
        }
      } else if (navLoaded || navTouchedRef.current) {
        // Toggled off — remove every link we manage for this page, but only when
        // we actually knew the prior state (navLoaded) or the user explicitly
        // toggled it this session. A transient load failure must not silently
        // delete a link the user never chose to remove.
        for (const link of links) {
          try {
            await deleteMenu(link._id);
          } catch (e) {
            /* ignore */
          }
        }
      }
    } catch (e) {
      message.warning(
        "Page saved, but updating its navigation link failed — you can adjust it under CMS Navigation."
      );
    }
  };

  const onSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (e) {
      return;
    }
    setSaving(true);
    try {
      const cleanSlug = normalizeSlugInput(values.slug);
      if (cleanSlug !== values.slug) form.setFieldsValue({ slug: cleanSlug });
      let url = bannerUrl;
      if (bannerFile) {
        const up = await uploadFile(bannerFile);
        url = up.url;
      }
      const payload = {
        title: values.title,
        slug: cleanSlug,
        status: values.status,
        body,
        banner: {
          type: values.bannerType || "image",
          url: url || "",
          caption: values.bannerCaption || "",
        },
        seo: {
          metaTitle: values.metaTitle || "",
          metaDescription: values.metaDescription || "",
        },
      };
      let savedId = editingId;
      if (editingId) {
        await updatePage(editingId, payload);
      } else {
        const created = await createPage(payload);
        savedId = created && created.page && created.page._id;
      }
      // Keep the navigation link in sync with the "Show in navigation" toggle.
      await syncNavLink(savedId, values);
      message.success(`Page ${editingId ? "updated" : "created"} successfully`);
      setModalOpen(false);
      load();
      loadMenu();
    } catch (e) {
      message.error(
        (e &&
          e.response &&
          e.response.data &&
          e.response.data.error &&
          e.response.data.error.message) ||
          "Save failed"
      );
    }
    setSaving(false);
  };

  // Enable / disable a page (published <-> draft). Optimistic, with rollback.
  const toggleStatus = async (record) => {
    const next = record.status === "published" ? "draft" : "published";
    setTogglingId(record._id);
    setRows((prev) =>
      prev.map((p) => (p._id === record._id ? { ...p, status: next } : p))
    );
    try {
      await updatePage(record._id, { status: next });
      message.success(
        next === "published" ? "Page enabled (now live)" : "Page disabled (hidden)"
      );
    } catch (e) {
      // rollback
      setRows((prev) =>
        prev.map((p) =>
          p._id === record._id ? { ...p, status: record.status } : p
        )
      );
      message.error("Could not update status");
    }
    setTogglingId(null);
  };

  const onDelete = (record) => {
    Modal.confirm({
      title: "Delete this page permanently?",
      content: (
        <span>
          “{record.title}” will be removed for good. To simply hide it from the
          website, disable it instead.
        </span>
      ),
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deletePage(record._id);
          // Remove the nav link(s) we manage for this page — but only LEAF links,
          // so we never cascade-delete sub-items someone nested under them in CMS
          // Navigation. (The public site already hides links to missing pages.)
          let items = menuItems;
          try {
            items = (await getMenuAll()).menu || menuItems;
          } catch (e) {
            /* fall back to snapshot */
          }
          for (const link of findLinkedMenus(record._id, items)) {
            const hasChildren = items.some(
              (m) => String(m.parent || "") === String(link._id)
            );
            if (!hasChildren) {
              try {
                await deleteMenu(link._id);
              } catch (e) {
                /* non-fatal */
              }
            }
          }
          message.success("Page deleted");
          load();
          loadMenu();
        } catch (e) {
          message.error("Delete failed");
        }
      },
    });
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const isOn = (r.status || "published") === "published";
      const hay = [
        r.title,
        r.slug,
        r.status,
        // Match the words users actually see in the UI, not just raw values.
        isOn ? "enabled live published on" : "disabled hidden draft off",
        r.seo && r.seo.metaTitle,
        r.banner && r.banner.caption,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search]);

  const publishedCount = useMemo(
    () => rows.filter((r) => r.status === "published").length,
    [rows]
  );

  const rowMenu = (record) => (
    <Menu>
      <Menu.Item key="edit" onClick={() => openEdit(record)}>
        <span className="flex items-center gap-2">
          <AiOutlineEdit /> Edit
        </span>
      </Menu.Item>
      <Menu.Item
        key="toggle"
        onClick={() => toggleStatus(record)}
        disabled={togglingId === record._id}
      >
        {record.status === "published" ? "Disable (hide)" : "Enable (publish)"}
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="delete" danger onClick={() => onDelete(record)}>
        <span className="flex items-center gap-2">
          <AiOutlineDelete /> Delete
        </span>
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: "Banner",
      dataIndex: ["banner", "url"],
      width: 96,
      render: (_, r) => {
        const url = r.banner && r.banner.url;
        const isVideo = r.banner && r.banner.type === "video";
        if (!url)
          return (
            <div className="cms-thumb-empty">
              <AiOutlinePicture />
            </div>
          );
        if (isVideo)
          return (
            <video className="cms-thumb" src={url} muted />
          );
        return (
          <Image
            src={url}
            className="cms-thumb"
            width={64}
            height={40}
            preview={false}
            style={{ objectFit: "cover", borderRadius: 8 }}
          />
        );
      },
    },
    {
      title: "Page",
      dataIndex: "title",
      sorter: (a, b) => (a.title || "").localeCompare(b.title || ""),
      render: (_, r) => (
        <div>
          <div className="cms-page-title">{r.title}</div>
          <span className="cms-page-slug">/{r.slug}</span>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 170,
      filters: [
        { text: "Enabled", value: "published" },
        { text: "Disabled", value: "draft" },
      ],
      onFilter: (value, r) => (r.status || "published") === value,
      render: (_, r) => {
        const on = r.status === "published";
        return (
          <Space>
            <Tooltip
              title={on ? "Live on the website — click to hide" : "Hidden — click to publish"}
            >
              <Switch
                checked={on}
                loading={togglingId === r._id}
                onChange={() => toggleStatus(r)}
                checkedChildren="On"
                unCheckedChildren="Off"
              />
            </Tooltip>
            <Tag color={on ? "green" : "default"}>
              {on ? "Enabled" : "Disabled"}
            </Tag>
          </Space>
        );
      },
    },
    {
      title: "Last updated",
      dataIndex: "updatedAt",
      width: 150,
      sorter: (a, b) =>
        new Date(a.updatedAt || 0) - new Date(b.updatedAt || 0),
      defaultSortOrder: "descend",
      render: (d) =>
        d ? (
          <span className="text-gray-500">
            {new Date(d).toLocaleDateString(undefined, {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </span>
        ) : (
          "—"
        ),
    },
    {
      title: "Actions",
      width: 130,
      render: (_, r) => (
        <div className="flex items-center gap-3">
          <a onClick={() => openEdit(r)}>Edit</a>
          <Dropdown overlay={rowMenu(r)} trigger={["click"]}>
            <a onClick={(e) => e.preventDefault()}>
              <AiOutlineMore size={20} />
            </a>
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <div className="content-panel px-4">
      <div className="mx-4">
        <Breadcrumb style={{ marginBottom: 20 }}>
          <Breadcrumb.Item>
            <Link to="/dashboard">Home</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>CMS Pages</Breadcrumb.Item>
        </Breadcrumb>
        <div className="flex justify-between items-center">
          <h1 className="page-heading">CMS Pages</h1>
          <button
            onClick={openAdd}
            className="bg-[#FA6210] h-12 px-5 font-semibold hover:bg-[#15538B] rounded-md text-white flex items-center gap-2"
          >
            <AiOutlinePlus /> Add Page
          </button>
        </div>
        <div className="border-b -mt-4 mb-6" />

        <div className="cms-stats">
          <div className="cms-stat">
            <div className="cms-stat-label">Total pages</div>
            <div className="cms-stat-value">{rows.length}</div>
          </div>
          <div className="cms-stat is-on">
            <div className="cms-stat-label">Enabled</div>
            <div className="cms-stat-value">{publishedCount}</div>
          </div>
          <div className="cms-stat is-off">
            <div className="cms-stat-label">Disabled</div>
            <div className="cms-stat-value">{rows.length - publishedCount}</div>
          </div>
        </div>

        <div className="cms-toolbar">
          <Input
            className="cms-search"
            size="large"
            allowClear
            prefix={<AiOutlineSearch className="text-gray-400" />}
            placeholder="Search by title, slug or status…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Tooltip title="Refresh">
            <Button size="large" onClick={load} icon={<AiOutlineReload />} />
          </Tooltip>
        </div>

        <Table
          className="cms-table"
          rowKey="_id"
          loading={loading}
          columns={columns}
          dataSource={filtered}
          pagination={{ pageSize: 10, showSizeChanger: true, hideOnSinglePage: true }}
          locale={{
            emptyText: (
              <Empty
                description={
                  search ? "No pages match your search" : "No pages yet"
                }
              />
            ),
          }}
        />
      </div>

      <Modal
        title={editingId ? "Edit Page" : "Add Page"}
        visible={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={onSubmit}
        confirmLoading={saving}
        width={860}
        okText={editingId ? "Save changes" : "Create page"}
        destroyOnClose
        afterClose={() => {
          // Release the object URL (via the bannerFile effect) and clear any
          // transient state so the next open always starts clean.
          setBannerFile(null);
          setBody("");
          setBannerUrl("");
          setBannerType("image");
          setEditingId(null);
          setSlugTouched(false);
          setShowInNav(false);
          setNavParent("top:main-menu");
          setLinkedMenuId(null);
          setNavLoaded(true);
          navTouchedRef.current = false;
        }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Title is required" }]}
          >
            <Input size="large" onChange={onTitleChange} placeholder="e.g. CAIE Admissions" />
          </Form.Item>
          <Form.Item
            name="slug"
            label="Slug (URL path)"
            rules={[{ required: true, message: "Slug is required" }]}
            extra="e.g. caie/admissions  ->  https://fatehacademy.com/caie/admissions"
          >
            <Input
              size="large"
              addonBefore="/"
              onChange={(e) => {
                setSlugTouched(true);
                // Drop a stray leading "/" as it's typed (the addon already
                // shows it); full cleanup happens on save.
                const stripped = e.target.value.replace(/^\/+/, "");
                if (stripped !== e.target.value) {
                  form.setFieldsValue({ slug: stripped });
                }
              }}
              placeholder="caie/admissions"
            />
          </Form.Item>
          <Form.Item
            name="status"
            label="Visibility"
            getValueProps={(v) => ({ checked: v === "published" })}
            getValueFromEvent={(checked) => (checked ? "published" : "draft")}
            extra="Enabled pages are live on the website. Disabled pages are hidden."
          >
            <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
          </Form.Item>

          <Form.Item
            label="Show in navigation menu"
            extra="Adds a clickable link to this page in the site menu. Off keeps the page reachable only by its URL."
          >
            <Switch
              checked={showInNav}
              onChange={(v) => {
                navTouchedRef.current = true;
                setShowInNav(v);
              }}
              checkedChildren="In menu"
              unCheckedChildren="Not in menu"
            />
          </Form.Item>
          {showInNav && (
            <Form.Item
              label="Add under"
              extra="Where the link appears. Its label is the page title; fine-tune later under CMS Navigation."
            >
              <Select
                value={navParent}
                onChange={(v) => {
                  navTouchedRef.current = true;
                  setNavParent(v);
                }}
                showSearch
                optionFilterProp="children"
              >
                {navParentOptions.map((o) => (
                  <Option key={o.value} value={o.value}>
                    {o.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <div className="font-medium mb-1">Page content</div>
          <div className="mb-5">
            <RichTextEditor
              key={editingId || "new"}
              value={body}
              onChange={setBody}
              uploadUrl={`${axios.defaults.baseURL || ""}/upload`}
              token={localStorage.getItem("accessToken")}
            />
          </div>

          <Form.Item name="bannerType" label="Page banner type">
            <Select onChange={(v) => setBannerType(v)}>
              <Option value="image">Image</Option>
              <Option value="video">Video</Option>
            </Select>
          </Form.Item>
          <Form.Item label="Page banner media (optional)">
            {(bannerPreview || bannerUrl) && (
              <div className="cms-banner-preview">
                {(
                  bannerFile
                    ? (bannerFile.type || "").startsWith("video")
                    : bannerType === "video"
                ) ? (
                  <video src={bannerPreview || bannerUrl} controls muted />
                ) : (
                  <img alt="banner preview" src={bannerPreview || bannerUrl} />
                )}
              </div>
            )}
            <Upload
              beforeUpload={(f) => {
                setBannerFile(f);
                return false;
              }}
              onRemove={() => setBannerFile(null)}
              maxCount={1}
              accept="image/*,video/mp4"
              listType="text"
            >
              <Button icon={<AiOutlineUpload />}>
                {bannerUrl || bannerPreview ? "Replace file" : "Select file"}
              </Button>
            </Upload>
          </Form.Item>
          <Form.Item name="bannerCaption" label="Banner caption (optional)">
            <Input />
          </Form.Item>
          <Form.Item name="metaTitle" label="SEO title (optional)">
            <Input />
          </Form.Item>
          <Form.Item name="metaDescription" label="SEO description (optional)">
            <Input.TextArea autoSize={{ minRows: 2 }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CmsPages;
